'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Check, X, Zap, Shield, TrendingUp, ChevronDown, Loader2, Mail } from 'lucide-react';
import { createCheckoutSession, getPricingSettings } from '@/lib/api-client';
import type { PricingSettings } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { useSite, useFeatures } from '@/context/SiteContext';
import { openContactForm } from '@/components/ui/ContactFormModal';

const DEFAULT_PRICING: PricingSettings = {
  proMonthlyPrice: 7.99,
  proMonthlyOriginalPrice: 0,
  proYearlyPrice: 59.99,
  proYearlyOriginalPrice: 0,
};

const FREE_FEATURES: { feature: string; free: boolean | string; pro: boolean | string; unlimited: boolean | string }[] = [
  // Market data
  { feature: 'Real-time quotes*',         free: true,          pro: true,          unlimited: true },
  { feature: 'Historical data',           free: '5 years',     pro: 'Full history', unlimited: 'Full history' },
  { feature: 'Intraday charts (1D, 1W)',  free: true,          pro: true,          unlimited: true },
  { feature: 'Technical indicators (SMA)',free: true,          pro: true,          unlimited: true },

  // Screening & discovery
  { feature: 'Stock screener',            free: 'Basic filters', pro: 'Advanced filters', unlimited: 'Advanced filters' },
  { feature: 'ETFs',                      free: true,          pro: true,          unlimited: true },
  { feature: 'IPO calendar',              free: true,          pro: true,          unlimited: true },
  { feature: 'Market heatmap',            free: true,          pro: true,          unlimited: true },
  { feature: 'News feed',                 free: true,          pro: true,          unlimited: true },
  { feature: 'Earnings calendar',         free: true,          pro: true,          unlimited: true },
  { feature: 'Top movers (gainers / losers)', free: true,      pro: true,          unlimited: true },
  { feature: 'Sector performance',        free: true,          pro: true,          unlimited: true },
  { feature: 'Market breadth & mood',     free: true,          pro: true,          unlimited: true },
  { feature: 'Analyst ratings & price targets', free: true,    pro: true,          unlimited: true },

  // Portfolios
  { feature: 'Portfolios',                free: '1',           pro: 'Unlimited',   unlimited: 'Unlimited' },
  { feature: 'Import portfolio (CSV / brokerage)', free: false, pro: true,         unlimited: true },
  { feature: 'Auto portfolio generation', free: false,         pro: true,          unlimited: true },
  { feature: 'Export data (CSV)',         free: false,         pro: true,          unlimited: true },
  { feature: 'Dashboard layouts',         free: '1',           pro: '10',          unlimited: '10' },

  // Congress & insider
  { feature: 'Congressional trading feed',free: true,          pro: true,          unlimited: true },
  { feature: 'Insider transactions',      free: true,          pro: true,          unlimited: true },
  { feature: 'Follow Congressional portfolios', free: '1',     pro: 'Unlimited',   unlimited: 'Unlimited' },
  { feature: 'Smart Money signals',       free: true,          pro: true,          unlimited: true },

  // Alerts & games
  { feature: 'Watchlist size',            free: '10 stocks',   pro: 'Unlimited',   unlimited: 'Unlimited' },
  { feature: 'Price alerts',              free: true,          pro: true,          unlimited: true },
  { feature: 'Portfolio challenges',      free: true,          pro: true,          unlimited: true },

  // Platform perks
  { feature: 'Ad-free experience',        free: false,         pro: true,          unlimited: true },
  { feature: 'API access',                free: false,         pro: false,         unlimited: true },
  { feature: 'Priority support',          free: false,         pro: true,          unlimited: true },
];

const faqs = [
  {
    q: 'What is included in the free plan?',
    a: "The free plan includes access to stock quotes (with 15-minute delay), basic financial data, a limited screener, and market news. It's a great way to get started with self-directed investing.",
  },
  {
    q: 'Can I cancel my Pro subscription at any time?',
    a: 'Yes! You can cancel your subscription at any time from your account settings. You will retain access to Pro features until the end of your current billing period.',
  },
  {
    q: 'Is there a free trial for Pro?',
    a: 'We offer a 7-day free trial for new Pro subscribers. No credit card required to start. You will only be charged after the trial ends if you choose to continue.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, Amex), PayPal, and Apple Pay. All payments are processed securely through Stripe.',
  },
  {
    q: "What is your refund policy?",
    a: "We offer a 30-day money-back guarantee. If you're not satisfied with Pro for any reason within the first 30 days, contact us for a full refund.",
  },
];

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check size={16} className="text-[#22c55e] mx-auto" />;
  if (value === false) return <X size={14} className="text-slate-600 mx-auto" />;
  return <span className="text-xs text-slate-300">{value}</span>;
}

export default function ProPage() {
  const { user, isPro, isFull } = useAuth();
  const { siteName } = useSite();
  const features = useFeatures();
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [pricing, setPricing] = useState<PricingSettings>(DEFAULT_PRICING);

  useEffect(() => {
    getPricingSettings().then(setPricing).catch(() => {});
  }, []);

  const stripeDisabled = features.loaded && !features.proSubscriptions;
  const isProMonthly = isPro && !isFull;

  const handleUpgrade = async () => {
    if (stripeDisabled) { openContactForm('upgrade'); return; }
    if (!user) { window.location.href = '/login?returnTo=/pro'; return; }
    setLoading(true);
    setCheckoutError('');
    try {
      const url = await createCheckoutSession();
      window.location.href = url;
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Unable to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-2 py-10">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 text-gold text-xs font-semibold px-3 py-1 rounded-full mb-4">
          <Zap size={12} /> Plan Offerings
        </div>
        <h1 className="text-4xl font-bold text-white mb-3">Unlock the Full Platform</h1>
        <p className="text-fg-muted text-lg max-w-xl mx-auto">
          Get real-time data, advanced tools, and unlimited access to everything {siteName} has to offer.
        </p>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        {/* Free */}
        <div className="bg-card-bg border border-default rounded-2xl p-6">
          <div className="mb-6">
            <div className="text-sm font-semibold text-fg-muted mb-2">Free</div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-white">$0</span>
              <span className="text-fg-subtle text-sm">/forever</span>
            </div>
            <p className="text-fg-subtle text-xs mt-2">Perfect for getting started</p>
          </div>
          {isFull ? (
            <button
              disabled
              className="block w-full text-center py-2.5 bg-card-hover-bg border border-default text-fg-subtle font-medium rounded-xl text-sm mb-6 cursor-not-allowed opacity-60"
            >
              Already on Pro Annual
            </button>
          ) : isProMonthly ? (
            <button
              disabled
              className="block w-full text-center py-2.5 bg-card-hover-bg border border-default text-fg-subtle font-medium rounded-xl text-sm mb-6 cursor-not-allowed opacity-60"
            >
              Already on Pro
            </button>
          ) : user ? (
            <button
              disabled
              className="block w-full text-center py-2.5 bg-card-hover-bg border border-default text-slate-300 font-medium rounded-xl text-sm mb-6 cursor-not-allowed opacity-80"
            >
              Current Plan
            </button>
          ) : (
            <Link
              href="/create-account"
              className="block w-full text-center py-2.5 bg-card-hover-bg hover:bg-border-default border border-default text-white font-medium rounded-xl text-sm transition-colors mb-6"
            >
              Get Started Free
            </Link>
          )}
          <ul className="space-y-2.5">
            {['Real-time quotes*', '5 years of history', 'Basic screener filters', '10-stock watchlist', '1 portfolio', '1 dashboard layout', 'Market news & heatmap', 'Congress & insider feeds'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-fg-muted">
                <Check size={14} className="text-slate-600 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Pro - Highlighted */}
        <div className="relative bg-gradient-to-b from-sky-500/10 to-card-bg border border-sky-500/40 rounded-2xl p-6">
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
            <span className="bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full">Most Popular</span>
          </div>
          <div className="mb-6">
            <div className="text-sm font-semibold text-sky-400 mb-2">Pro</div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">${pricing.proMonthlyPrice.toFixed(2)}</span>
              <span className="text-fg-subtle text-sm">/month</span>
              {pricing.proMonthlyOriginalPrice > pricing.proMonthlyPrice && (
                <span className="text-slate-600 text-sm line-through">${pricing.proMonthlyOriginalPrice.toFixed(2)}</span>
              )}
            </div>
            <p className="text-fg-subtle text-xs mt-2">For serious investors</p>
          </div>
          <button
            onClick={handleUpgrade}
            disabled={loading || isPro}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl text-sm transition-colors mb-2 shadow-lg shadow-sky-500/20"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : stripeDisabled && !isPro ? <Mail size={14} /> : null}
            {isFull ? 'Already on Pro Annual' : isProMonthly ? 'Already on Pro' : stripeDisabled ? 'Contact us to upgrade' : 'Start 7-Day Free Trial'}
          </button>
          {checkoutError && (
            <p className="text-red-400 text-xs mb-4 text-center">{checkoutError}</p>
          )}
          <ul className="space-y-2.5">
            {['Real-time quotes*', 'Full history', 'Advanced screener (50+ filters)', 'Unlimited watchlist', 'Unlimited portfolios', '10 dashboard layouts', 'CSV import & export', 'Auto portfolio generation', 'Ad-free experience', 'Priority support'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                <Check size={14} className="text-sky-400 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Pro Annual / Full */}
        <div className="bg-card-bg border border-gold/30 rounded-2xl p-6">
          <div className="mb-6">
            <div className="text-sm font-semibold text-gold mb-2">Pro Annual</div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-white">${pricing.proYearlyPrice.toFixed(2)}</span>
              <span className="text-fg-subtle text-sm">/year</span>
              {pricing.proYearlyOriginalPrice > pricing.proYearlyPrice && (
                <span className="text-slate-600 text-sm line-through">${pricing.proYearlyOriginalPrice.toFixed(2)}</span>
              )}
            </div>
            {(() => {
              const effective = pricing.proYearlyOriginalPrice > pricing.proYearlyPrice
                ? pricing.proYearlyOriginalPrice
                : pricing.proMonthlyPrice * 12;
              const saved = effective - pricing.proYearlyPrice;
              return saved > 0
                ? <p className="text-fg-subtle text-xs mt-2">Best value — save ${saved.toFixed(2)}</p>
                : <p className="text-fg-subtle text-xs mt-2">Best value plan</p>;
            })()}
          </div>
          <button
            onClick={handleUpgrade}
            disabled={loading || isFull}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:from-card-hover-bg disabled:to-card-hover-bg disabled:text-fg-subtle text-black font-semibold rounded-xl text-sm transition-colors mb-6 shadow-lg shadow-amber-500/10"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : stripeDisabled && !isFull ? <Mail size={14} /> : null}
            {isFull ? 'Already on Pro Annual' : stripeDisabled ? 'Contact us to upgrade' : 'Get Unlimited Access'}
          </button>
          <ul className="space-y-2.5">
            {['Everything in Pro', 'API access', 'Best annual value', 'Priority support', 'Locked-in pricing'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                <Check size={14} className="text-gold flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Feature comparison table */}
      <div className="bg-card-bg border border-default rounded-2xl overflow-hidden mb-12">
        <div className="p-5 border-b border-default">
          <h2 className="text-lg font-semibold text-white">Feature Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-default">
                <th className="px-5 py-3 text-left text-sm font-medium text-fg-muted w-1/2">Feature</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-fg-muted">Free</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-sky-400">Pro</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gold">Full</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default/50">
              {FREE_FEATURES.map((row) => (
                <tr key={row.feature} className="hover:bg-card-hover-bg/50 transition-colors">
                  <td className="px-5 py-3 text-sm text-slate-300">{row.feature}</td>
                  <td className="px-4 py-3 text-center"><FeatureValue value={row.free} /></td>
                  <td className="px-4 py-3 text-center bg-sky-500/5"><FeatureValue value={row.pro} /></td>
                  <td className="px-4 py-3 text-center"><FeatureValue value={row.unlimited} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-[11px] text-fg-subtle border-t border-default/50">
          * Real-time quotes are sourced from the IEX exchange feed. Prices can diverge by a few cents from the consolidated SIP/NBBO during fast markets.
        </p>
      </div>

      {/* Trust badges */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <div className="flex items-center gap-4 bg-card-bg border border-default rounded-xl p-4">
          <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <Shield size={20} className="text-[#22c55e]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">30-Day Guarantee</div>
            <div className="text-xs text-fg-subtle">Full refund if not satisfied</div>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-card-bg border border-default rounded-xl p-4">
          <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
            <Zap size={20} className="text-sky-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">Cancel Anytime</div>
            <div className="text-xs text-fg-subtle">No lock-in, no contracts</div>
          </div>
        </div>
        <div className="flex items-center gap-4 bg-card-bg border border-default rounded-xl p-4">
          <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
            <TrendingUp size={20} className="text-indigo-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">7-Day Free Trial</div>
            <div className="text-xs text-fg-subtle">No credit card required</div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mb-10">
        <h2 className="text-xl font-bold text-white mb-6 text-center">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <details key={idx} className="group bg-card-bg border border-default rounded-xl overflow-hidden">
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                <span className="text-sm font-medium text-white">{faq.q}</span>
                <ChevronDown size={16} className="text-fg-muted group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
              </summary>
              <div className="px-5 pb-4 text-sm text-fg-muted leading-relaxed">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="text-center bg-gradient-to-r from-card-bg to-card-hover-bg border border-sky-500/20 rounded-2xl p-10">
        <h2 className="text-2xl font-bold text-white mb-2">Ready to level up your research?</h2>
        <p className="text-fg-muted mb-6">Join smart investors using {siteName} Pro to sharpen their research.</p>
        <button
          onClick={handleUpgrade}
          disabled={loading || isPro}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors shadow-lg shadow-sky-500/20"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : stripeDisabled && !isPro ? <Mail size={16} /> : null}
          {isFull ? 'Already on Pro Annual' : isProMonthly ? 'Already on Pro' : stripeDisabled ? 'Contact us to upgrade' : 'Start Free Trial'}
        </button>
        {checkoutError && (
          <p className="text-red-400 text-xs mt-2">{checkoutError}</p>
        )}
        {!stripeDisabled && (
          <p className="text-slate-600 text-xs mt-3">No credit card required · Cancel anytime · Starting at ${pricing.proMonthlyPrice.toFixed(2)}/mo</p>
        )}
      </div>
    </div>
  );
}
